import classNames from 'classnames';
import React, { useState } from 'react';
import { MdCheck, MdClose, MdInfoOutline } from 'react-icons/md';
import { Button } from 'lib/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from 'lib/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'lib/components/ui/tooltip';
import useUser from 'modules/User/hooks/useUser';
import useSubscription from 'modules/Billing/hooks/useSubscription';
import CountdownDate from 'modules/common/components/CountdownDate';
import PlanBadge from './PlanBadge';
import config from 'lib/config';
import dayjs from 'lib/helpers/date';
import { useAuthModal } from 'modules/auth';
import { track } from 'lib/analytics/client';
import { plans } from '../config/plans';

type PricingV2Props = React.HTMLAttributes<HTMLDivElement> & {
  withSubscribeButton?: boolean;
};

type BillingPeriod = 'monthly' | 'annual';

type PlanTier = 'standard' | 'plus' | 'vip' | 'ultim';

type Feature = {
  name: string;
  description: string;
  tooltip?: string;
  availability: Record<PlanTier, boolean>;
};

const features: Feature[] = [
  {
    name: 'Accès CVthèque',
    description: 'Valorise tes candidats, accélère tes placements',
    tooltip:
      "Dépose tes profils qualifiés dans la CVthèque Headlinker. Chaque membre peut y trouver le candidat idéal pour sa mission et toi, tu perçois ta prime si c'est le tien qui est retenu. Plus tu as de candidats, plus tu multiplies tes chances de placement.",
    availability: {
      standard: true,
      plus: true,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Accès missions',
    description: 'Publie ou rejoins des missions pour booster ton activité',
    tooltip:
      "Poste tes propres missions dans l'espace ou rejoins celles publiées par d'autres membres. Idéal pour augmenter tes revenus, occuper les creux d'activité ou te lancer sereinement dans le conseil en recrutement.",
    availability: {
      standard: true,
      plus: true,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Messagerie directe + cafés/apéros',
    description: 'Échange avec tes pairs, partage tes questions',
    tooltip:
      "Accède à la messagerie Headlinker pour échanger en privé avec les autres membres. Participe aussi aux cafés et apéros virtuels réguliers pour networker et partager tes retours d'expérience.",
    availability: {
      standard: true,
      plus: true,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Groupes de discussion',
    description: 'Pose tes questions et interagis avec la communauté',
    tooltip: 'Accède aux groupes de discussion Headlinker pour échanger sur ton secteur spécifique ou sur des sujets généraux.',
    availability: {
      standard: true,
      plus: true,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Créer des groupes de discussion privés',
    description: "Crée tes propres espaces d'échange avec d'autres membres",
    tooltip: 'Crée tes propres groupes de discussion privés et invite les membres de ton choix pour échanger sur des sujets spécifiques.',
    availability: {
      standard: false,
      plus: true,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Intégration LinkedIn',
    description: 'Partage tes candidats en un clic avec notre extension',
    tooltip: 'Super simple, il suffit de l\'installer et un bouton "Partager sur Headlinker" apparait sur les profils LinkedIn de tes candidats.',
    availability: {
      standard: true,
      plus: true,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Intégration Wink',
    description: 'Partage tes candidats en un clic depuis Wink',
    tooltip:
      "Super simple, il suffit de générer un code d'intégration et de le coller dans Wink, et tu peux en un clic partager tes candidats sur Headlinker.",
    availability: {
      standard: true,
      plus: true,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Masterclass',
    description: '45 min actionnables + replays',
    tooltip:
      'Sessions animées par des experts (recrutement, outils, mindset, prospection, etc.). Format court, actionnable, avec replay accessible à vie.',
    availability: {
      standard: false,
      plus: true,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Exclusivité sourcing',
    description: 'Décuple tes chances de succès sur un sourcing',
    tooltip:
      'Si un autre membre te confie sa mission en exclusivité, tu es le seul à pouvoir proposer des candidats dessus pendant la durée définie. Aucune concurrence interne, toutes les cartes en main pour réussir ton placement.',
    availability: {
      standard: false,
      plus: true,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Bons plans',
    description: 'Accède aux offres partagées par la communauté',
    tooltip:
      'Profite des bons plans proposés par les membres : licences disponibles, outils en promo, opportunités de collaboration. Partage également tes propres bons plans avec la communauté.',
    availability: {
      standard: false,
      plus: true,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Sessions de prospection',
    description: '2 fois par semaine dans un espace virtuel dédié',
    tooltip:
      "Rejoins les sessions de prospection collective les mardis et jeudis. Un espace virtuel dédié, du co-working, de l'entraide, et une dynamique de groupe pour booster ta prosp'.",
    availability: {
      standard: false,
      plus: true,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Signale toi "Disponible pour sourcer"',
    description: 'Badge visible + prise de contact directe',
    tooltip:
      'Active un badge sur ton profil indiquant que tu es ouvert(e) au sourcing. Les autres membres peuvent te contacter directement pour te proposer leurs missions.',
    availability: {
      standard: false,
      plus: true,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Propose une exclu sourcing',
    description: "Assigne une mission à un autre recruteur et ne reçois pas d'autres sollicitations",
    availability: {
      standard: false,
      plus: true,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Réductions partenaires',
    description: 'Tarifs négociés sur les meilleurs outils',
    tooltip: 'Profite de réductions exclusives négociées avec nos partenaires : outils de sourcing, jobboards, solutions RH, etc.',
    availability: {
      standard: false,
      plus: true,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Rejoins une équipe',
    description: 'Sors de la solitude, rejoins une équipe ambitieuse',
    tooltip:
      "Crée ou intègre une équipe avec d'autres recruteurs. Travaillez en mode projet, partagez vos candidats et missions, et développez une vraie synergie collective.",
    availability: {
      standard: false,
      plus: false,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Headlinker Match',
    description: 'Matching sourceur/recruteur en 24–72h',
    tooltip:
      'Dorothée (la fondatrice) te trouve personnellement un sourceur adapté à ta mission en 24 à 72h. Service premium pour maximiser tes chances de succès.',
    availability: {
      standard: false,
      plus: false,
      vip: true,
      ultim: true,
    },
  },

  {
    name: 'Fiches membres SEO (Soon)',
    description: 'Visibilité Google + lien partageable',
    tooltip:
      'Ta fiche membre est indexée sur Google et optimisée SEO. Tu peux la partager (LinkedIn, signature mail, etc.) pour renforcer ta crédibilité et attirer de nouveaux clients.',
    availability: {
      standard: false,
      plus: false,
      vip: true,
      ultim: true,
    },
  },

  {
    name: 'Mastermind Headlinker',
    description: 'Dorothée te présente des gens de ton niveau (Clubs 100k€, 200k€, 300k€)',
    tooltip: `🔥 Sélection à l'entrée sur critères de CA (>200k€) et de personnalité (par moi, j'assume)
🔥 Groupes de 5 personnes
🔥 Une réunion par semaine : chacun fixe ses priorités devant les autres
🔥 Un groupe WhatsApp et un espace GoBrunch dédié pour soumettre ses sujets instantanément (facturation, nouvelles offres, CGV...)
🔥 Toutes les 5 semaines, un membre expose une problématique et les autres l'aident (rotation hebdomadaire)
🔥 Intervention mensuelle d'un expert (trésorerie, fiscalité, investissements, outsourcing...)
🔥 Cross-business et suivi des processus sur la plateforme Headlinker
🔥 Réunion en présentiel au moins une fois par an
🔥 Ambition : augmenter le CA de 100k€ sur l'année`,
    availability: {
      standard: false,
      plus: false,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Mise en relation entreprise',
    description: 'Intro qualifiée via notre vitrine',
    tooltip:
      "Headlinker reçoit régulièrement des demandes d'entreprises cherchant un recruteur. Si ton profil correspond, Dorothée te met en relation directement.",
    availability: {
      standard: false,
      plus: false,
      vip: true,
      ultim: true,
    },
  },
  {
    name: 'Commission sur placement',
    description: 'Exemption des 10% de commission payable à Headlinker',
    tooltip:
      'Sur les missions créées dans Headlinker, une commission de 10 % est prélevée sur chaque prime (sourceur et recruteur titulaire de la mission). Avec le plan Ultim, cette commission passe à 0 % : tu gardes 100 % de tes primes.',
    availability: {
      standard: false,
      plus: false,
      vip: false,
      ultim: true,
    },
  },
];

const getButtonVariant = (planId: PlanTier): 'plus' | 'vip' | 'ultim' | 'outline' => {
  switch (planId) {
    case 'plus':
      return 'plus';
    case 'vip':
      return 'vip';
    case 'ultim':
      return 'ultim';
    default:
      return 'outline';
  }
};

const PricingV2: React.FC<PricingV2Props> = ({ className, withSubscribeButton = true, ...props }) => {
  const { hasPermission, user } = useUser();
  const { createSubscription, loadingSubscription, deleteSubscription, deletingSubscription } = useSubscription();
  const { toggleModal } = useAuthModal();
  // État pour chaque plan individuellement
  const [planPeriods, setPlanPeriods] = useState<Record<PlanTier, BillingPeriod>>({
    standard: 'monthly',
    plus: 'annual',
    vip: 'monthly',
    ultim: 'monthly',
  });

  const subscribe = (plan: string) => () => {
    track('subscription.clicked', { plan });
    if (!user) {
      toggleModal();
      return;
    }
    return createSubscription({ plan });
  };

  // Déterminer le plan actuel de l'utilisateur
  const getCurrentPlan = (): PlanTier | null => {
    if (hasPermission('pro') || hasPermission('ultim')) {
      return 'ultim';
    }

    if (hasPermission('teambooster') || hasPermission('vip')) {
      return 'vip';
    }

    if (hasPermission('booster') || hasPermission('starter') || hasPermission('starter_239') || hasPermission('plus')) {
      return 'plus';
    }

    if (!hasPermission('paying')) {
      return 'standard';
    }

    return null;
  };

  const currentPlan = getCurrentPlan();

  const visiblePlans = plans.filter((plan) => {
    // Si le plan est visible uniquement pour les non-payants
    if (plan.visibleOnlyForNonPaying) {
      return user && !hasPermission('paying') && (hasPermission('earlyadopter') || hasPermission('free'));
    }
    // Sinon, afficher le plan
    return true;
  });

  // Vérifier si l'offre spéciale est active et si l'utilisateur n'est pas payant
  // const isSpecialOfferActive = dayjs().isBefore(dayjs(config.specialOfferForHistoricMembers.endAt));
  const canUse239 = hasPermission('earlyadopter_onhold_239');
  // const canUseSpecialOffer = (isSpecialOfferActive && hasPermission('earlyadopter')) || canUse239;
  const canUseSpecialOffer = false;

  return (
    <TooltipProvider>
      <div className={classNames('w-full', className)} {...props}>
        {/* Countdown pour offre spéciale membres Histori */}
        {canUseSpecialOffer && (
          <div className="mb-6 bg-gradient-to-r from-primary to-accent text-white rounded-lg p-6 text-center">
            <div className="space-y-2">
              <h3 className="text-xl font-bold">Offre spéciale membres historiques</h3>
              <p className="text-sm opacity-90">
                Profite de tarifs exceptionnels jusqu'au <strong>{dayjs(config.specialOfferForHistoricMembers.endAt).format('DD MMMM hh:mm')}</strong>
                &nbsp;!
              </p>
              <div className="flex justify-center mt-4">
                <CountdownDate
                  date={config.specialOfferForHistoricMembers.endAt}
                  className="text-white font-extrabold justify-center flex gap-3 lowercase"
                  type="countdown-simple"
                />
              </div>
            </div>
          </div>
        )}

        {/* Pricing Table */}
        <div className="w-full overflow-x-auto pt-5">
          <div className="min-w-max">
            <table className="w-full border-collapse bg-white rounded-lg shadow-lg">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="p-4 text-left bg-gray-50 min-w-[250px] max-w-[300px] md:min-w-[350px] md:max-w-[400px]">
                    <div className="font-semibold text-gray-700">Fonctionnalités</div>
                  </th>
                  {visiblePlans.map((plan) => {
                    const period = planPeriods[plan.id];
                    const pricing = plan[period];
                    const canStill239 = canUse239 && plan.id === 'plus' && period === 'annual';

                    let price = canUseSpecialOffer ? pricing.discountedPrice : pricing.price;

                    return (
                      <th
                        key={`${plan.id}-${period}`}
                        className={classNames('p-4 text-center min-w-[180px] relative', plan.color, {
                          'border-l-4 border-r-4 border-t-4 border-primary': plan.highlighted,
                        })}
                      >
                        {plan.badge && (
                          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                            <span className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">{plan.badge}</span>
                          </div>
                        )}
                        <div className={classNames('flex justify-center', { 'mt-2': plan.badge })}>
                          <PlanBadge type={plan.id as any} size="sm" className="text-base" />
                        </div>

                        <div className="mt-2 flex flex-col items-center gap-0.5">
                          <div className="flex items-center justify-center gap-2">
                            {canUseSpecialOffer && pricing.price > 0 && (
                              <span className={classNames('text-lg font-medium line-through', plan.textColor, 'opacity-50')}>{pricing.price}€</span>
                            )}
                            <span className={classNames('text-2xl font-extrabold', plan.textColor)}>
                              {price > 0 ? `${canStill239 ? 239 : price}€` : 'Gratuit'}
                            </span>
                          </div>
                          {!plan.visibleOnlyForNonPaying && (
                            <ToggleGroup
                              type="single"
                              value={period}
                              onValueChange={(value: BillingPeriod) => {
                                if (value) {
                                  setPlanPeriods((prev) => ({
                                    ...prev,
                                    [plan.id]: value,
                                  }));
                                }
                              }}
                              appearance="segments"
                              size="sm"
                            >
                              {!plan.monthly.hidden && <ToggleGroupItem value="monthly">/mois</ToggleGroupItem>}
                              {!plan.annual.hidden && <ToggleGroupItem value="annual">/an</ToggleGroupItem>}
                            </ToggleGroup>
                          )}

                          {/* Bouton rapide */}
                          {pricing.stripePlan && currentPlan !== plan.id && (
                            <Button
                              variant={getButtonVariant(plan.id)}
                              onClick={subscribe(canStill239 ? 'starter_yearly_239' : (pricing.stripePlan as any))}
                              loading={loadingSubscription}
                              className="w-full max-w-[120px] mt-1"
                              size="sm"
                            >
                              Souscrire
                            </Button>
                          )}
                          {currentPlan === plan.id && (
                            <div className={classNames('text-sm font-bold mt-2 py-1 px-3 rounded-full', plan.color, plan.textColor)}>
                              ✓ Plan actuel
                            </div>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {features.map((feature, index) => (
                  <tr key={index} className={classNames('border-b border-gray-100', { 'bg-gray-50': index % 2 === 0 })}>
                    <td className={classNames('p-4 w-[100px]', { 'border-l-4 border-primary': false })}>
                      <div className="flex items-center text-left gap-1.5">
                        <div className="font-medium text-gray-900">{feature.name}</div>
                        {feature.tooltip && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className="text-gray-400 hover:text-gray-600 mt-auto ml-auto">
                                <MdInfoOutline className="w-4 h-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <span className="text-xs whitespace-pre-line">{feature.tooltip}</span>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      {feature.description && <div className="text-sm text-left text-gray-500 mt-1">{feature.description}</div>}
                    </td>
                    {visiblePlans.map((plan) => (
                      <td
                        key={plan.id}
                        className={classNames('p-4 text-center', {
                          'border-l-4 border-r-4 border-primary': plan.highlighted,
                        })}
                      >
                        {feature.availability[plan.id] ? (
                          <div className="flex justify-center">
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                              <MdCheck className="w-5 h-5 text-green-600" />
                            </div>
                          </div>
                        ) : (
                          <div className="flex justify-center">
                            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                              <MdClose className="w-5 h-5 text-red-600" />
                            </div>
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="bg-white border-t-2 border-gray-200">
                  <td className="p-4 w-[100px]"></td>
                  {visiblePlans.map((plan) => (
                    <td
                      key={plan.id}
                      className={classNames('p-4 text-center', {
                        'border-l-4 border-r-4 border-primary': plan.highlighted,
                      })}
                    ></td>
                  ))}
                </tr>
                <tr className="bg-white">
                  <td className="p-4"></td>
                  {visiblePlans.map((plan) => {
                    const period = planPeriods[plan.id];
                    const pricing = plan[period];
                    return (
                      <td
                        key={plan.id}
                        className={classNames('p-4 text-center', {
                          'border-l-4 border-r-4 border-b-4 border-primary': plan.highlighted,
                        })}
                      >
                        {!pricing.stripePlan ? (
                          <Button variant="outline" disabled className="w-full">
                            Gratuit
                          </Button>
                        ) : currentPlan === plan.id ? (
                          <div className="space-y-2">
                            <div className={classNames('text-base font-bold py-2 px-4 rounded-lg', plan.color, plan.textColor)}>✓ Plan actuel</div>
                            <Button
                              variant="destructive"
                              onClick={() => deleteSubscription({ subscriptionId: 'notimplemented' })}
                              loading={deletingSubscription}
                              className="w-full"
                              size="sm"
                            >
                              Annuler
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant={getButtonVariant(plan.id)}
                            onClick={subscribe(pricing.stripePlan as any)}
                            loading={loadingSubscription}
                            className="w-full"
                          >
                            Souscrire
                          </Button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default PricingV2;
